/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { isArray } from 'betterplace-utils'
import { get } from 'lodash'
import { type HTMLInputTypeAttribute, type JSX, useMemo } from 'react'
import { type FieldPath, type FieldValues, type RegisterOptions } from 'react-hook-form'
import { useActionFormContext } from '..'
import type { FieldAsPropType, FieldProps } from './types'
import type { Operation } from '@/helpers/operations'
function getType(defaultValue: any, type: string | undefined): HTMLInputTypeAttribute | undefined {
  if (type) return type as HTMLInputTypeAttribute
  if (typeof defaultValue === 'boolean' || Array.isArray(defaultValue)) return 'checkbox'
  return undefined
}

function getDefaultChecked(defaultValue: any, value: any, type: HTMLInputTypeAttribute): boolean {
  if (type !== 'checkbox' && type !== 'radio') return false
  if (type === 'radio') return defaultValue === value
  if (typeof defaultValue === 'boolean' || !defaultValue) return !!defaultValue
  if (defaultValue === value) return true
  if (isArray(defaultValue)) return (defaultValue as unknown[]).includes(value)
  return false
}

function getDefaultProps(
  defaultValue: any,
  value: any,
  type: HTMLInputTypeAttribute | undefined
): { defaultValue?: any } | { defaultChecked?: boolean } | undefined {
  if (type === 'checkbox' || type === 'radio') return { defaultChecked: getDefaultChecked(defaultValue, value, type) }
  return defaultValue !== undefined ? { defaultValue } : undefined
}

// Remember to provide the type prop to the Field component in case of using radio or checkbox inputs
function Field<
  TFields extends FieldValues,
  TField extends FieldPath<TFields>,
  TInput extends FieldAsPropType = 'input',
>({ name, as, type: type_, ...props }: FieldProps<TFields, TField, TInput>) {
  const {
    onBlur,
    onChange,
    validate,
    valueAsDate,
    valueAsNumber,
    setValueAs,
    min,
    max,
    maxLength,
    minLength,
    pattern,
    required,
    shouldUnregister,
    value,
    ...inputProps
  } = props
  const Tag: any = as ?? 'input'
  const { register, defaultValues } = useActionFormContext<TFields, Operation<any, any>>()
  const defaultValue = useMemo(() => get(defaultValues, name), [defaultValues, name])
  const type = useMemo(() => getType(defaultValue, type_), [defaultValue, type_])
  const defaultProps = useMemo(() => getDefaultProps(defaultValue, value, type), [defaultValue, value, type])
  return (
    <Tag
      {...inputProps}
      value={value}
      type={type}
      {...register(name, {
        onBlur,
        onChange,
        validate,
        valueAsDate,
        valueAsNumber,
        setValueAs,
        min,
        max,
        maxLength,
        minLength,
        pattern,
        required,
        shouldUnregister,
      } as RegisterOptions<TFields, TField>)}
      min={min}
      max={max}
      {...defaultProps}
    />
  )
}

export function WithFormValues<TFormValues extends FieldValues>() {
  return Field as <TField extends FieldPath<TFormValues>, TInput extends FieldAsPropType = 'input'>(
    props: FieldProps<TFormValues, TField, TInput>
  ) => JSX.Element
}

export default Field
